
import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuth } from './providers/AuthProvider';
import { usePendingEnrollmentRedirect } from './hooks/usePendingEnrollmentRedirect';

// Layouts
import { MarketingLayout } from './layouts/MarketingLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { ProtectedRoute } from './ProtectedRoute';

// Public Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import RoleSelection from './pages/RoleSelection';
import ChooseRole from './pages/ChooseRole';
import Index from './pages/Index';
import { PublicCoursePage } from './pages/PublicCoursePage';
import CreatorProfile from './pages/CreatorProfile';
import NotFound from './pages/NotFound';
import Checkout from './pages/Checkout';
import GlobalCheckout from "./pages/GlobalCheckout";
import StudentAuth from "./pages/StudentAuth"; // Restore the StudentAuth page

// Protected Pages
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import Sales from './pages/Sales';
import Students from './pages/Students';
import Settings from './pages/Settings';
import ProfileEditor from './pages/ProfileEditor';
import CourseEditor from './pages/CourseEditor';
import AdminDashboard from './pages/AdminDashboard';
import StudentDashboard from './pages/StudentDashboard';
import CoursePlayer from './pages/CoursePlayer';
import MyNotes from './pages/MyNotes';
import StudentSettings from './pages/StudentSettings';
import UsernameSetup from './pages/UsernameSetup';
import RoleBasedRedirect from './pages/RoleBasedRedirect';

const App = () => {
  const { session, user, loading } = useAuth();
  // Ensure this hook is called unconditionally
  usePendingEnrollmentRedirect();

  // Debug auth state
  React.useEffect(() => {
    console.log("[App] Auth state:", { 
      hasSession: !!session, 
      userId: user?.id,
      loading,
      timestamp: new Date().toISOString()
    });
  }, [session, user, loading]);

  return (
    <>
      <Toaster />
      
      <Routes>
        {/* 1. MARKETING ROUTES - with MainNav + Footer */}
        <Route path="/" element={<MarketingLayout />}>
          <Route index element={<Index />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="not-found" element={<NotFound />} />
          {/* Renamed from "checkout" to "global-checkout" to avoid route confusion */}
          <Route path="global-checkout" element={<GlobalCheckout />} /> 
        </Route>

        {/* 2. PUBLIC COURSE/PROFILE ROUTES - no chrome */}
        <Route element={<PublicLayout />}>
          {/* IMPORTANT: Put specific routes before wildcard routes */}
          <Route path=":username/:courseSlug/checkout" element={<Checkout />} /> {/* Course-specific checkout route */}
          <Route path=":username/:courseSlug" element={<PublicCoursePage />} />
          
          {/* Restore the StudentAuth route for enrollment flows */}
          <Route path="student-auth" element={<StudentAuth />} />
          
          {/* Place the dynamic username route after specific routes */}
          <Route path=":username" element={<CreatorProfile />} />
        </Route>
        
        {/* 3. AUTH & ONBOARDING ROUTES - no chrome */}
        <Route element={<AuthLayout />}>
          <Route path="choose-role" element={<ChooseRole />} />
          <Route path="role-selection" element={<RoleSelection />} />
          <Route path="onboarding/username" element={<UsernameSetup />} />
        </Route>

        {/* 4. PROTECTED ROUTES */}
        <Route element={<ProtectedRoute />}>
          {/* Dashboard root redirect */}
          <Route path="dashboard" element={<RoleBasedRedirect />} />
          
          {/* Creator Dashboard Routes */}
          <Route path="dashboard/creator" element={<Dashboard />} />
          <Route path="dashboard/courses" element={<Courses />} />
          <Route path="dashboard/courses/:courseId/edit" element={<CourseEditor />} />
          <Route path="dashboard/sales" element={<Sales />} />
          <Route path="dashboard/students" element={<Students />} />
          <Route path="dashboard/profile" element={<ProfileEditor />} />
          <Route path="dashboard/settings" element={<Settings />} />
          <Route path="dashboard/creator/settings" element={<Settings />} />
          
          {/* Student Dashboard Routes */}
          <Route path="dashboard/student" element={<StudentDashboard />} />
          <Route path="dashboard/student/my-learning" element={<StudentDashboard />} />
          <Route path="dashboard/student/my-learning/course/:courseId" element={<CoursePlayer />} />
          <Route path="dashboard/student/notes" element={<MyNotes />} />
          <Route path="dashboard/student/settings" element={<StudentSettings />} />

          {/* Admin Routes */}
          <Route path="admin/dashboard/*" element={<AdminDashboard />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
    </>
  );
};

export default App;
